/**
 * Pure utility functions for bracket snap logic.
 * Extracted from BracketOverlay for testability.
 */

export interface NotePosition {
  timestamp: number;
  x: number;
  width: number;
}

/**
 * Convert a world-space X position to a timestamp by interpolating
 * between note positions.
 *
 * When worldX is past the last note, returns slightly past the last onset
 * (+ 0.001) so that the end-handle's "last onset strictly before rawEnd"
 * logic can include the last note.
 */
export function worldXToTimestamp(worldX: number, notePositions: NotePosition[]): number {
  if (notePositions.length === 0) return 0;

  const sortedByX = [...notePositions].sort((a, b) => a.x - b.x);

  if (worldX <= sortedByX[0].x) return sortedByX[0].timestamp;

  if (worldX >= sortedByX[sortedByX.length - 1].x) {
    return sortedByX[sortedByX.length - 1].timestamp + 0.001;
  }

  for (let i = 0; i < sortedByX.length - 1; i++) {
    const left = sortedByX[i];
    const right = sortedByX[i + 1];

    if (worldX >= left.x && worldX <= right.x) {
      const ratio = (worldX - left.x) / (right.x - left.x);
      return left.timestamp + ratio * (right.timestamp - left.timestamp);
    }
  }

  return sortedByX[0].timestamp;
}

/**
 * Find the last onset in a sorted array that is strictly less than rawEnd.
 * Used by the end-handle snap: "include all notes the user dragged past."
 * Returns the onset value, or the first onset as fallback.
 */
export function findLastOnsetStrictlyBefore(noteOnsets: number[], rawEnd: number): number {
  if (noteOnsets.length === 0) {
    throw new Error('noteOnsets must not be empty');
  }

  for (let i = noteOnsets.length - 1; i >= 0; i--) {
    if (noteOnsets[i] < rawEnd) {
      return noteOnsets[i];
    }
  }

  return noteOnsets[0];
}

/**
 * Build a map of onset → minimum offset across all notes at that onset.
 * When a chord has mixed durations (e.g. quarter melody + held bass),
 * the min offset corresponds to the shorter note the user is visually
 * targeting. Using max would jump the bracket end to the held bass
 * note's offset, far past what the user expects.
 */
export function buildMinOffsetByOnset(
  notes: Array<{ timestamp: number; duration: number; isRest: boolean }>,
): Map<number, number> {
  const minOffsetByOnset = new Map<number, number>();

  for (const note of notes) {
    if (note.isRest) continue;
    const onset = note.timestamp;
    const offset = onset + note.duration;
    const existing = minOffsetByOnset.get(onset);
    if (existing === undefined || offset < existing) {
      minOffsetByOnset.set(onset, offset);
    }
  }

  return minOffsetByOnset;
}

/**
 * Compute the new start and end timestamps when a handle is dragged past
 * the opposite handle. Instead of swapping or collapsing, the bracket
 * shifts while maintaining its width.
 *
 * @param draggedTimestamp - The raw timestamp where the handle was dragged to
 * @param currentStart - The bracket's current start timestamp
 * @param currentEnd - The bracket's current end timestamp
 * @param handle - Which handle is being dragged ('start' or 'end')
 * @returns The new start and end timestamps
 */
export function shiftBracketOnOverlap(
  draggedTimestamp: number,
  currentStart: number,
  currentEnd: number,
  handle: 'start' | 'end',
): { start: number; end: number } {
  const bracketWidth = currentEnd - currentStart;

  if (handle === 'start') {
    if (draggedTimestamp >= currentEnd) {
      return { start: draggedTimestamp, end: draggedTimestamp + bracketWidth };
    }
    return { start: draggedTimestamp, end: currentEnd };
  }

  if (draggedTimestamp <= currentStart) {
    return { start: draggedTimestamp - bracketWidth, end: draggedTimestamp };
  }
  return { start: currentStart, end: draggedTimestamp };
}

/**
 * Re-snap a shifted bracket end to align with phrase boundaries.
 * When a bracket is shifted (drag start past end), the raw shifted end
 * may extend into the next phrase's pickup measure. This function caps
 * the end at the next phrase boundary after the bracket start.
 *
 * @param rawEnd - The raw shifted end (start + prevWidth)
 * @param snappedStart - The snapped start position
 * @param noteOnsets - Sorted array of all note onsets
 * @param minOffsetByOnset - Map of onset → min offset
 * @param phraseStartTimestamps - Sorted array of phrase start timestamps
 * @returns The re-snapped end timestamp
 */
export function reSnapShiftedEnd(
  rawEnd: number,
  snappedStart: number,
  noteOnsets: number[],
  minOffsetByOnset: Map<number, number>,
  phraseStartTimestamps: number[],
): number {
  const nextPhraseStart = phraseStartTimestamps.find((ts) => ts > snappedStart + 0.01);
  const reSnapCap = nextPhraseStart !== undefined ? Math.min(rawEnd, nextPhraseStart) : rawEnd;

  let reSnapIdx = -1;
  for (let i = noteOnsets.length - 1; i >= 0; i--) {
    if (noteOnsets[i] < reSnapCap) {
      reSnapIdx = i;
      break;
    }
  }
  const reSnapOnset = reSnapIdx !== -1 ? noteOnsets[reSnapIdx] : noteOnsets[noteOnsets.length - 1];
  const reSnapOffset = minOffsetByOnset.get(reSnapOnset);
  return reSnapOffset !== undefined ? reSnapOffset : rawEnd;
}

/**
 * Compute the default bracket end timestamp for the first line of a score.
 *
 * Uses the next phrase's start timestamp (which accounts for pickup/anacrusis
 * measures and trailing rests). Falls back to the last note's offset when
 * there is only one phrase.
 */
export function computeFirstLineEndTimestamp(
  phrases: Array<{
    startTimestamp: number;
    notes: Array<{ timestamp: number; duration: number; isRest: boolean }>;
  }>,
): number | null {
  if (phrases.length === 0) return null;

  if (phrases.length > 1) {
    return phrases[1].startTimestamp;
  }

  const nonRestNotes = phrases[0].notes.filter((n) => !n.isRest);
  if (nonRestNotes.length === 0) return null;

  const lastNote = nonRestNotes[nonRestNotes.length - 1];
  return lastNote.timestamp + lastNote.duration;
}
